import { useEffect, useState, type ReactElement } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Dices } from 'lucide-react';

import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { DataPagination } from '@/ui/DataPagination';
import { ErrorState } from '@/ui/ErrorState';
import { MobileCardList, type ItemDoCard } from '@/ui/MobileCardList';
import { PortalHero } from '@/ui/PortalHero';
import { Spinner } from '@/ui/Spinner';
import { StatusBadge } from '@/ui/StatusBadge';

import { capaDaPagina } from '../imagensDeCapa';

import {
  data,
  ROTULO_STATUS_SORTEIO,
  VARIANTE_STATUS_SORTEIO,
  type SorteioPublicoResumo,
} from './contrato';
import { useSorteiosPublicos } from './dados';

const caminhoDoSorteio = (s: SorteioPublicoResumo) =>
  `/dados-publicos/sorteios/${encodeURIComponent(s.id)}`;

/**
 * Resultado dos sorteios do credenciamento — `/dados-publicos/sorteios`.
 *
 * Aberta a qualquer pessoa, sem entrar no sistema (D-07). Cada linha é o
 * sorteio de um ciclo de inscrição; a linha inteira abre o resultado e a
 * verificação (regra 9 de interface). Paginação no servidor.
 */
export function SorteiosPublicosPage(): ReactElement {
  const navigate = useNavigate();
  const [pagina, setPagina] = useState(1);
  const [tamanho, setTamanho] = useState(20);
  const consulta = useSorteiosPublicos(pagina, tamanho);

  useEffect(() => {
    document.title = 'Resultado dos sorteios — Sistema TEG';
  }, []);

  const linhas = consulta.data?.itens ?? [];
  const total = consulta.data?.total ?? 0;

  const colunas: DataTableColumn<SorteioPublicoResumo>[] = [
    {
      id: 'ciclo',
      header: 'Ciclo de inscrição',
      // Caminho de teclado até o detalhe; o mouse clica a linha inteira.
      cell: (s) => (
        <Link
          to={caminhoDoSorteio(s)}
          className="focus-visible:ring-ring rounded font-medium hover:underline focus:outline-hidden focus-visible:ring-2"
        >
          {s.cicloNome}
        </Link>
      ),
    },
    {
      id: 'situacao',
      header: 'Situação',
      cell: (s) => (
        <StatusBadge
          status={VARIANTE_STATUS_SORTEIO[s.status]}
          label={ROTULO_STATUS_SORTEIO[s.status]}
        />
      ),
    },
    { id: 'participantes', header: 'Participantes', cell: (s) => s.totalParticipantes },
    { id: 'vagas', header: 'Vagas', cell: (s) => s.totalVagas },
    { id: 'preparado', header: 'Preparado em', cell: (s) => data(s.preparadoEm) },
    { id: 'executado', header: 'Executado em', cell: (s) => data(s.executadoEm) },
    { id: 'publicado', header: 'Publicado em', cell: (s) => data(s.publicadoEm) },
  ];

  const cartoes: ItemDoCard[] = linhas.map((s) => ({
    id: s.id,
    title: (
      <Link
        to={caminhoDoSorteio(s)}
        className="focus-visible:ring-ring rounded hover:underline focus:outline-hidden focus-visible:ring-2"
      >
        {s.cicloNome}
      </Link>
    ),
    badges: (
      <StatusBadge
        status={VARIANTE_STATUS_SORTEIO[s.status]}
        label={ROTULO_STATUS_SORTEIO[s.status]}
      />
    ),
    fields: [
      { label: 'Participantes', value: s.totalParticipantes },
      { label: 'Vagas', value: s.totalVagas },
      { label: 'Executado em', value: data(s.executadoEm) },
      { label: 'Publicado em', value: data(s.publicadoEm) },
    ],
    onClick: () => navigate(caminhoDoSorteio(s)),
  }));

  return (
    <div>
      <PortalHero
        image={capaDaPagina('dados-publicos')}
        title="Resultado dos sorteios"
        description="O sorteio do credenciamento, ciclo a ciclo: quem foi contemplado, a lista de espera e como conferir, você mesmo, que o resultado não foi alterado."
        compact
      />

      <div className="mx-auto w-full max-w-5xl space-y-6 px-4 py-10 sm:px-6">
        <section aria-label="Sorteios">
          {consulta.isPending ? (
            <Spinner size="lg" rotulo="Carregando os sorteios" />
          ) : consulta.isError ? (
            <ErrorState
              variant="load-failed"
              title="Não foi possível carregar os sorteios"
              description="Verifique sua conexão e tente de novo em instantes."
              primaryAction={{ label: 'Tentar de novo', onClick: () => void consulta.refetch() }}
            />
          ) : (
            <>
              <div className="sm:hidden">
                <MobileCardList
                  items={cartoes}
                  emptyIcon={Dices}
                  emptyTitle="Nenhum sorteio ainda"
                  emptyDescription="Quando um ciclo de inscrição for sorteado, o resultado aparece aqui."
                />
                {total > tamanho && (
                  <DataPagination
                    page={pagina}
                    perPage={tamanho}
                    total={total}
                    onPageChange={setPagina}
                    onPerPageChange={(t) => {
                      setTamanho(t);
                      setPagina(1);
                    }}
                  />
                )}
              </div>
              <div className="hidden min-w-0 sm:block">
                <DataTable
                  columns={colunas}
                  data={linhas}
                  isLoading={consulta.isFetching && !consulta.isPending}
                  total={total}
                  page={pagina}
                  perPage={tamanho}
                  onPageChange={setPagina}
                  onPerPageChange={(t) => {
                    setTamanho(t);
                    setPagina(1);
                  }}
                  onRowClick={(s) => navigate(caminhoDoSorteio(s))}
                  linhaComElementoInterativo
                  emptyIcon={Dices}
                  emptyTitle="Nenhum sorteio ainda"
                  emptyDescription="Quando um ciclo de inscrição for sorteado, o resultado aparece aqui."
                  ariaLabel="Sorteios do credenciamento"
                />
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

export default SorteiosPublicosPage;
